/*
 * Defaults from the leaf when a widget is bound for the first time.
 *
 * Leaf properties and author decisions are kept apart:
 *
 *  - Unit and enum texts belong to the leaf. They are NOT copied into the
 *    project; the renderers read them at runtime (`hmi/leafMeta.ts`). A copy
 *    would go stale as soon as someone maintains the leaf.
 *  - Caption, decimals and scale are decisions of the widget (required
 *    fields or fields with a default in the C# contract). They are prefilled
 *    from the leaf on binding and belong to the author afterwards.
 *
 * Status LED colour mappings are not created here either: that rule belongs
 * to the server (`StatusLedMappingFactory`, AI suggestion), and without a
 * mapping the LED shows the leaf's display text.
 *
 * Only on the FIRST binding: whoever swaps a leaf has already made their
 * choices.
 *
 * Type-only imports, so the Node test run can load the file.
 */

import type { HmiLeafMeta } from '../leafMeta';
import type { WidgetDefinition } from '../widgets';

type Lookup = (bindingKey: string) => HmiLeafMeta | undefined;

/** Primary binding field per widget kind; only these kinds get defaults. */
const PRIMARY_FIELD: Partial<Record<WidgetDefinition['kind'], string>> = {
  gauge: 'valuePoint',
  'rolling-counter': 'valuePoint',
  'value-tile': 'valuePoint',
  sparkline: 'valuePoint',
  'status-led': 'statePoint',
  'setpoint-stepper': 'feedbackPoint',
};

/** Kinds whose `decimals` has a fixed default in the contract. The value
 *  tile is left out on purpose: for it, "empty" already means "as the leaf". */
const HAS_DECIMALS = new Set<WidgetDefinition['kind']>(['gauge', 'rolling-counter', 'setpoint-stepper']);

/** Scale the catalog gives a new gauge. While it is still set, the author
 *  has not chosen one. */
const FACTORY_GAUGE_SCALE = { min: 0, max: 100 };

export function applyLeafDefaults(
  prev: WidgetDefinition,
  next: WidgetDefinition,
  lookup: Lookup | null,
): WidgetDefinition {
  if (!lookup || prev.kind !== next.kind) return next;
  const field = PRIMARY_FIELD[next.kind];
  if (!field) return next;

  const before = (prev as unknown as Record<string, unknown>)[field];
  const after = (next as unknown as Record<string, unknown>)[field];
  if (!isEmptyBinding(before) || isEmptyBinding(after)) return next;

  const meta = lookup(after as string);
  if (!meta) return next;

  const out = { ...next } as Record<string, unknown>;
  if (!next.label && meta.label) out.label = meta.label;

  if (meta.decimals != null && HAS_DECIMALS.has(next.kind)) out.decimals = meta.decimals;

  const range = meta.minValue != null && meta.maxValue != null && meta.minValue < meta.maxValue
    ? { min: meta.minValue, max: meta.maxValue }
    : null;
  if (range) {
    if (next.kind === 'gauge' && next.min === FACTORY_GAUGE_SCALE.min && next.max === FACTORY_GAUGE_SCALE.max) {
      Object.assign(out, range);
    }
    if (next.kind === 'setpoint-stepper') Object.assign(out, range);
  }
  return out as unknown as WidgetDefinition;
}

function isEmptyBinding(v: unknown): boolean {
  return typeof v !== 'string' || v.trim() === '';
}
